import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, MapPin, Clock, CheckCircle2, Circle, Flame, Loader2,
  Route, Home, Wheat, Dumbbell, Briefcase, Mic, Radio, AlertTriangle, Sparkles, Beef,
  StickyNote, Plus, Trash2, BookText, ChevronDown,
  Star, MessageCircle, HelpCircle, RefreshCw, Trophy, TrendingUp, Users, Megaphone, Newspaper, ExternalLink, Coins, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RunDiscoveryButton } from "@/components/AthleteCard";
import { IntelCard } from "@/components/IntelCard";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  platformMeta, relativeDate, exactDate, rankEvents, DISCIPLINE_COLORS, freshnessScore,
} from "@/lib/format";

const ALL_PLATFORMS = ["instagram", "facebook", "tiktok", "x", "threads", "youtube", "news", "web", "podcasts", "reddit", "media_guide"];

const SECTIONS = [
  { key: "fresh", label: "Fresh Intel", icon: Flame, fresh: true },
  { key: "road", label: "On The Road", icon: Route, cats: ["ROAD LIFE"] },
  { key: "family", label: "Family & Home", icon: Home, cats: ["FAMILY", "PERSONAL LIFE"] },
  { key: "ranch", label: "Horses & Ranch", icon: Wheat, cats: ["HORSES", "RANCH LIFE", "OTHER ANIMALS"] },
  { key: "hobbies", label: "Hobbies & Interests", icon: Dumbbell, cats: ["HOBBIES", "PURCHASES / EQUIPMENT"] },
  { key: "business", label: "Business & Sponsors", icon: Briefcase, cats: ["BUSINESS", "SPONSORS"] },
  { key: "media", label: "Podcasts & Interviews", icon: Mic, cats: ["MEDIA"] },
  { key: "community", label: "Community & Health", icon: Radio, cats: ["COMMUNITY", "HEALTH / INJURY", "BEHIND THE SCENES", "OTHER"] },
  { key: "rodeo", label: "Rodeo", icon: Radio, cats: ["RODEO"] },
];

export default function AthleteProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, setCredits, buyCredits } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("intel");
  const [polling, setPolling] = useState(false);
  const [stock, setStock] = useState([]);
  const [newNote, setNewNote] = useState("");

  const load = async () => {
    const d = await api.getIntel(id);
    setData(d);
    return d;
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    await api.addNote(id, newNote.trim());
    setNewNote("");
    load();
  };
  const removeNote = async (noteId) => {
    await api.deleteNote(id, noteId);
    load();
  };

  useEffect(() => {
    api.athleteStock(id).then(setStock).catch(() => setStock([]));
  }, [id]);

  useEffect(() => {
    load().then((d) => {
      // resume the live spinner if a run is genuinely in progress
      if (d.discovery_status === "running") {
        const started = d.athlete?.discovery_started_epoch || 0;
        if (Date.now() / 1000 - started < 900) setPolling(true);
      }
      // No auto-scan — a fresh scan costs a credit and must be triggered manually.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!polling) return;
    const t = setInterval(async () => {
      const d = await load();
      if (d.discovery_status !== "running") {
        setPolling(false);
        if (d.discovery_status === "done") toast.success("Interview intel updated");
        if (d.discovery_status === "error") toast.error("Discovery failed — try again");
      }
    }, 5000);
    return () => clearInterval(t);
  }, [polling]);

  const runDiscovery = async () => {
    const isRefresh = !!data?.last_discovery;
    try {
      const r = await api.discover(id);
      if (typeof r.credits === "number" && !isAdmin) setCredits(r.credits);
      setData((p) => ({ ...p, discovery_status: "running" }));
      setPolling(true);
      toast.info(
        isAdmin
          ? "Scanning public sources — this can take a couple of minutes"
          : `${isRefresh ? "Refreshing" : "Scanning"} — 1 credit used · this can take a couple of minutes`
      );
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("You're out of credits.", {
          description: "Grab 25 more credits for $25 to keep scanning.",
          action: { label: "Buy 25 credits ($25)", onClick: () => buyCredits() },
          duration: 8000,
        });
      } else {
        toast.error(e?.response?.data?.detail || "Could not start the scan");
      }
    }
  };

  const events = data?.events || [];
  const mgEvents = useMemo(() => events.filter((e) => e.origin === "media_guide"), [events]);
  const grouped = useMemo(() => {
    const feed = events.filter((e) => e.origin !== "media_guide");
    const map = {};
    SECTIONS.forEach((s) => {
      if (s.fresh) map[s.key] = rankEvents(feed).slice(0, 5);
      else map[s.key] = rankEvents(feed.filter((e) => s.cats.includes(e.category)));
    });
    return map;
  }, [events]);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-32 text-[#686B77]">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading brief…
      </div>
    );
  }

  const a = data.athlete;
  const startedEpoch = a.discovery_started_epoch || 0;
  const stale = data.discovery_status === "running" && Date.now() / 1000 - startedEpoch > 900;
  const running = data.discovery_status === "running" && !stale;
  const effectiveStatus = stale ? "error" : data.discovery_status;
  const checked = new Set(data.sources_checked || []);
  const sourceNotes = a.source_notes || {};
  const topStanding = (a.standings || []).slice().sort((x, y) => x.rank - y.rank)[0];

  return (
    <div className="max-w-[1700px] mx-auto px-5 md:px-8 py-8">
      <button
        data-testid="back-button"
        onClick={() => nav("/")}
        className="flex items-center gap-2 text-sm text-[#9A9CA6] hover:text-[#F3EFE6] transition-colors mb-5"
      >
        <ArrowLeft className="h-4 w-4" /> All athletes
      </button>

      {/* Low-balance nudge (regular users) */}
      {!isAdmin && user && !user.comped && user.credits <= 3 && (
        <div data-testid="low-credit-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-[#E8833A]/40 bg-[#E8833A]/10 px-5 py-4">
          <div className="flex items-center gap-3">
            <Coins className="h-5 w-5 text-[#E8833A] shrink-0" />
            <div>
              <div className="font-display font-bold text-[#F3EFE6]">
                {user.credits <= 0 ? "You're out of credits" : `Only ${user.credits} credit${user.credits === 1 ? "" : "s"} left`}
              </div>
              <div className="text-sm text-[#9A9CA6]">Each fresh scan or refresh uses 1 credit. Grab 25 more for $25.</div>
            </div>
          </div>
          <button
            data-testid="low-credit-buy-button"
            onClick={() => buyCredits()}
            className="shrink-0 px-5 py-2.5 rounded-lg font-semibold text-sm bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors"
          >
            Buy 25 credits — $25
          </button>
        </div>
      )}

      {/* Comped account note */}
      {!isAdmin && user && user.comped && (
        <div data-testid="comped-banner" className="mb-6 flex items-center gap-3 rounded-xl border border-[#334155] bg-[#1A202C] px-5 py-3">
          <ShieldCheck className="h-5 w-5 text-[#94A3B8] shrink-0" />
          <div className="text-sm text-[#B8BAC4]">
            <span className="font-semibold text-[#F3EFE6]">Comped account.</span> Each scan or refresh uses 1 credit, billed directly by the organizer — no card needed.
          </div>
        </div>
      )}

      {/* Hero header */}
      <section className="rounded-2xl border border-[#262933] bg-[#12141A] p-6 md:p-8 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-[#F3EFE6]">
              {a.known_name}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-[#9A9CA6]">
              <span className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4" /> {a.hometown || "—"} · {a.country}
              </span>
              <span className="font-mono text-[#E8833A] uppercase text-xs tracking-widest">
                {a.association}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(a.disciplines || []).map((d) => (
                <span
                  key={d}
                  className="px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider font-semibold border"
                  style={{
                    color: DISCIPLINE_COLORS[d] || "#9A9CA6",
                    borderColor: `${DISCIPLINE_COLORS[d] || "#9A9CA6"}44`,
                    backgroundColor: `${DISCIPLINE_COLORS[d] || "#9A9CA6"}14`,
                  }}
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-start lg:items-end gap-3">
            <RunDiscoveryButton onClick={() => runDiscovery()} status={effectiveStatus} isAdmin={isAdmin} />
            {running && a.discovery_stage ? (
              <div className="font-mono text-[11px] text-[#E8833A] flex items-center gap-1.5" data-testid="discovery-stage">
                <Loader2 className="h-3 w-3 animate-spin" />
                {a.discovery_stage}
              </div>
            ) : (
              <div className="font-mono text-[11px] text-[#686B77] flex items-center gap-1.5" data-testid="last-scan-date">
                <Clock className="h-3 w-3" />
                {data.last_discovery
                  ? `Last scanned ${relativeDate(data.last_discovery)} · ${exactDate(data.last_discovery)}`
                  : "Not scanned yet"}
              </div>
            )}
          </div>
        </div>

        {/* Announcer notes */}
        <div className="mt-5 pt-5 border-t border-[#1E212A]">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#686B77] mb-2 flex items-center gap-1.5">
            <StickyNote className="h-3 w-3 text-[#E8833A]" /> Announcer notes {(a.notes || []).length > 0 && `(${a.notes.length})`}
          </div>
          {(a.notes || []).length > 0 && (
            <div className="space-y-1.5 mb-2">
              {a.notes.map((n) => (
                <div key={n.id} data-testid="athlete-note" className="flex items-start justify-between gap-2 px-3 py-1.5 rounded-lg bg-[#1A1D26] border border-[#262933]">
                  <span className="text-sm text-[#F3EFE6]">{n.text}</span>
                  <button data-testid="delete-note" onClick={() => removeNote(n.id)} className="text-[#686B77] hover:text-[#FB923C] transition-colors shrink-0 mt-0.5">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              data-testid="note-input"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addNote()}
              placeholder='e.g. #1 Saddle Bronc in the World'
              className="flex-1 h-10 bg-[#0F1116] border-[#262933] text-[#F3EFE6] placeholder:text-[#686B77] focus-visible:ring-[#E8833A]"
            />
            <button data-testid="add-note" onClick={addNote} className="flex items-center gap-1.5 px-4 rounded-lg text-sm font-semibold bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
          <p className="text-[11px] text-[#686B77] mt-1.5">These notes are pulled straight into the presenter run sheet.</p>
        </div>

        {/* Interests */}
        {(a.interests || []).length > 0 && (
          <div className="mt-5 pt-5 border-t border-[#1E212A]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#686B77] mb-2 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-[#E8833A]" /> Learned public interests
            </div>
            <div className="flex flex-wrap gap-1.5">
              {a.interests.map((it) => (
                <span key={it} className="px-2 py-0.5 rounded-md bg-[#1A1D26] border border-[#262933] text-xs text-[#B8BAC4]">
                  {it}
                </span>
              ))}
            </div>
          </div>
        )}

        {stock.length > 0 && (
          <div className="mt-5 pt-5 border-t border-[#1E212A]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#686B77] mb-2 flex items-center gap-1.5">
              <Beef className="h-3 w-3 text-[#E8833A]" /> Stock drawn ({stock.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {stock.map((s) => (
                <Link
                  key={s.id}
                  to={`/animal/${s.id}`}
                  data-testid="drawn-stock-item"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1A1D26] border border-[#262933] hover:border-[#E8833A]/50 hover:-translate-y-0.5 transition-transform duration-150"
                >
                  <Beef className="h-3.5 w-3.5 text-[#E8833A]" />
                  <span className="text-sm text-[#F3EFE6] font-medium">
                    {(s.brand_code || s.brand_number) && (
                      <span className="font-mono text-[#E8833A] mr-1">{s.brand_code} {s.brand_number}</span>
                    )}
                    {s.name}
                  </span>
                  <span className="font-mono text-[10px] text-[#686B77]">
                    {s.animal_type} · {s.contractor}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Sources checked bar */}
      <section className="rounded-xl border border-[#262933] bg-[#0F1116] p-4 mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#686B77] mb-3">
          Sources checked
        </div>
        <div className="flex flex-wrap gap-2">
          {ALL_PLATFORMS.map((p) => {
            const m = platformMeta(p);
            const Icon = m.icon;
            const on = checked.has(p);
            const limited = !!sourceNotes[p];
            return (
              <div
                key={p}
                data-testid={`source-status-${p}`}
                title={sourceNotes[p] || ""}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs",
                  limited
                    ? "border-[#4D3E1B] bg-[#1B1710] text-[#FACC15]"
                    : on
                    ? "border-[#1B4D36] bg-[#0F1B15] text-[#4ADE80]"
                    : "border-[#262933] bg-[#12141A] text-[#686B77]"
                )}
              >
                <Icon className="h-3.5 w-3.5" style={{ color: on ? undefined : "#686B77" }} />
                <span className="text-[#B8BAC4]">{m.label}</span>
                {limited ? (
                  <AlertTriangle className="h-3.5 w-3.5" />
                ) : on ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <Circle className="h-3 w-3" />
                )}
              </div>
            );
          })}
        </div>
        {Object.keys(sourceNotes).length > 0 && (
          <div className="mt-3 space-y-1">
            {Object.entries(sourceNotes).map(([p, note]) => (
              <div key={p} data-testid={`source-note-${p}`} className="flex items-start gap-1.5 text-[11px] text-[#9A8A5A]">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-[#FACC15]" />
                <span><span className="font-semibold text-[#FACC15]">{platformMeta(p).label}:</span> {note}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-[#262933]">
        {[
          { k: "brief", l: "Presenter Brief" },
          { k: "intel", l: "Interview Intel" },
          { k: "accounts", l: `Social Accounts (${data.accounts.length})` },
          { k: "rodeo", l: "Rodeo Standings" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={cn(
              "px-4 py-3 text-sm font-medium transition-colors duration-200 border-b-2 -mb-px",
              tab === t.k ? "border-[#E8833A] text-[#F3EFE6]" : "border-transparent text-[#9A9CA6] hover:text-[#F3EFE6]"
            )}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "brief" && (
        <PresenterBriefTab
          brief={data.presenter_brief}
          events={events}
          athleteId={id}
          onRefresh={load}
          running={running}
        />
      )}
      {tab === "intel" && (
        <IntelTab
          grouped={grouped}
          events={events}
          mgEvents={mgEvents}
          unverified={data.unverified || []}
          running={running}
          stage={a.discovery_stage}
          status={effectiveStatus}
          onRun={() => runDiscovery()}
          isAdmin={isAdmin}
        />
      )}
      {tab === "accounts" && <AccountsTab accounts={data.accounts} athleteId={id} onChange={load} />}
      {tab === "rodeo" && <RodeoTab standings={a.standings || []} top={topStanding} events={grouped.rodeo} />}
    </div>
  );
}

const IntelTab = ({ grouped, events, mgEvents = [], unverified, running, stage, status, onRun, isAdmin }) => {
  const [mgOpen, setMgOpen] = useState(true);
  if (events.length === 0) {
    return (
      <div className="text-center py-20 rounded-xl border border-dashed border-[#262933] bg-[#0F1116]">
        {running ? (
          <>
            <Loader2 className="h-8 w-8 text-[#E8833A] animate-spin mx-auto mb-4" />
            <p className="text-[#F3EFE6] font-display text-lg">{stage || "Scanning the public web…"}</p>
            <p className="text-[#9A9CA6] text-sm mt-1">Two-pass grounded search · this can take a couple of minutes</p>
          </>
        ) : (
          <>
            <Sparkles className="h-8 w-8 text-[#686B77] mx-auto mb-4" />
            <p className="text-[#F3EFE6] font-display text-lg">No intel gathered yet</p>
            <p className="text-[#9A9CA6] text-sm mt-1 mb-5">Run discovery to build this athlete's interview brief.</p>
            <div className="flex justify-center">
              <RunDiscoveryButton onClick={onRun} status={status} isAdmin={isAdmin} />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {mgEvents.length > 0 && (
        <section data-testid="intel-section-media-guide">
          <button
            data-testid="media-guide-toggle"
            onClick={() => setMgOpen((o) => !o)}
            className="w-full flex items-center gap-2 mb-4 group"
          >
            <BookText className="h-5 w-5 text-[#E8833A]" />
            <h2 className="font-display font-bold text-xl text-[#F3EFE6]">Media Guide</h2>
            <span className="font-mono text-xs text-[#686B77]">{mgEvents.length} · official CPRA bio</span>
            <ChevronDown className={cn("h-4 w-4 text-[#686B77] ml-auto transition-transform duration-200", mgOpen && "rotate-180")} />
          </button>
          {mgOpen && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {mgEvents.map((ev) => (
                <IntelCard key={ev.id} ev={ev} />
              ))}
            </div>
          )}
        </section>
      )}

      {SECTIONS.map((s) => {
        const items = grouped[s.key];
        if (!items || items.length === 0) return null;
        return (
          <section key={s.key} data-testid={`intel-section-${s.key}`}>
            <div className="flex items-center gap-2 mb-4">
              <s.icon className="h-5 w-5 text-[#E8833A]" />
              <h2 className="font-display font-bold text-xl text-[#F3EFE6]">{s.label}</h2>
              <span className="font-mono text-xs text-[#686B77]">{items.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {items.map((ev) => (
                <IntelCard key={ev.id} ev={ev} />
              ))}
            </div>
          </section>
        );
      })}

      {unverified.length > 0 && (
        <section data-testid="intel-section-unverified">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-[#94A3B8]" />
            <h2 className="font-display font-bold text-xl text-[#94A3B8]">Needs Verification</h2>
            <span className="font-mono text-xs text-[#686B77]">
              {unverified.length} · not shown in main feed
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 opacity-80">
            {unverified.map((ev) => (
              <IntelCard key={ev.id} ev={ev} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const CONF_BADGE = {
  CONFIRMED: "bg-[#122A1E] text-[#4ADE80] border-[#1B4D36]",
  PROBABLE: "bg-[#2A2312] text-[#FACC15] border-[#4D3E1B]",
  POSSIBLE: "bg-[#1A202C] text-[#94A3B8] border-[#334155]",
};

const SOCIAL_PLATFORMS = ["instagram", "facebook", "tiktok", "x", "threads", "youtube", "website"];

const AccountsTab = ({ accounts, athleteId, onChange }) => {
  const [platform, setPlatform] = useState("instagram");
  const [url, setUrl] = useState("");

  const act = async (id, body) => {
    await api.updateAccount(id, body);
    toast.success("Account updated");
    onChange();
  };
  const addCorrect = async () => {
    if (!url.trim()) return toast.error("Paste the account URL");
    await api.addAccount(athleteId, { platform, profile_url: url.trim(), confidence: "CONFIRMED" });
    toast.success("Account saved & verified");
    setUrl("");
    onChange();
  };

  return (
    <div className="space-y-4">
      {/* Add / correct account */}
      <div data-testid="add-account-form" className="rounded-xl border border-[#E8833A]/30 bg-[#12141A] p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#686B77] mb-2">
          Add / correct an account
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            data-testid="add-account-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="bg-[#0F1116] border border-[#262933] rounded-lg px-3 py-2 text-sm text-[#F3EFE6] focus:outline-none focus:ring-1 focus:ring-[#E8833A] sm:w-40"
          >
            {SOCIAL_PLATFORMS.map((p) => <option key={p} value={p}>{platformMeta(p).label}</option>)}
          </select>
          <Input
            data-testid="add-account-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Paste the correct profile URL…"
            className="flex-1 bg-[#0F1116] border-[#262933] text-[#F3EFE6] focus-visible:ring-[#E8833A]"
          />
          <button
            data-testid="add-account-save"
            onClick={addCorrect}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors shrink-0"
          >
            Save & verify
          </button>
        </div>
        <p className="text-[11px] text-[#686B77] mt-2">Rejected the wrong match? Add the real account here — it's saved as CONFIRMED and used for intel.</p>
      </div>

      {accounts.length === 0 ? (
        <div className="text-center py-14 rounded-xl border border-dashed border-[#262933] bg-[#0F1116] text-[#9A9CA6]">
          No accounts discovered yet. Run discovery, or add the correct one above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {accounts.map((acc) => {
        const m = platformMeta(acc.platform);
        const Icon = m.icon;
        return (
          <div key={acc.id} data-testid="account-card" className="rounded-xl border border-[#262933] bg-[#12141A] p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="h-9 w-9 rounded-lg bg-[#1A1D26] border border-[#262933] flex items-center justify-center" style={{ color: m.color }}>
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <div className="min-w-0">
                  <div className="text-[#F3EFE6] font-medium">{m.label}</div>
                  <a href={acc.profile_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#7FB2E0] hover:underline truncate block max-w-[220px]">
                    {acc.profile_url}
                  </a>
                </div>
              </div>
              <span className={cn("px-2 py-0.5 rounded-md border text-[10px] font-mono uppercase tracking-wider font-semibold", CONF_BADGE[acc.confidence])}>
                {acc.confidence}
              </span>
            </div>
            {acc.evidence && <p className="text-xs text-[#9A9CA6] mt-3 leading-relaxed">{acc.evidence}</p>}
            <div className="flex items-center gap-2 mt-4">
              {acc.manually_verified ? (
                <span className="text-xs font-mono text-[#4ADE80] flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Verified
                </span>
              ) : acc.rejected ? (
                <span className="text-xs font-mono text-[#94A3B8]">Rejected</span>
              ) : (
                <>
                  <button
                    data-testid="account-confirm-button"
                    onClick={() => act(acc.id, { manually_verified: true, rejected: false })}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#122A1E] text-[#4ADE80] border border-[#1B4D36] hover:bg-[#16341f] transition-colors"
                  >
                    Confirm
                  </button>
                  <button
                    data-testid="account-reject-button"
                    onClick={() => act(acc.id, { rejected: true, manually_verified: false })}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#2A1812] text-[#FB923C] border border-[#4D261B] hover:bg-[#331d15] transition-colors"
                  >
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
        </div>
      )}
    </div>
  );
};

const RodeoTab = ({ standings, events }) => {
  return (
    <div className="space-y-8">
      <div className="rounded-xl border border-[#262933] bg-[#12141A] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#262933] text-left font-mono text-[10px] uppercase tracking-widest text-[#686B77]">
              <th className="px-5 py-3">Event</th>
              <th className="px-5 py-3">CPRA Rank</th>
              <th className="px-5 py-3">Earnings</th>
              <th className="px-5 py-3">Rodeos</th>
            </tr>
          </thead>
          <tbody>
            {standings.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-6 text-[#686B77] text-center">No standings on record</td></tr>
            ) : standings.slice().sort((a, b) => a.rank - b.rank).map((s, i) => (
              <tr key={i} data-testid="standing-row" className="border-b border-[#1A1D26] last:border-0">
                <td className="px-5 py-3 text-[#F3EFE6]" style={{ color: DISCIPLINE_COLORS[s.event] }}>{s.event}</td>
                <td className="px-5 py-3 font-mono text-[#E8833A] font-semibold">#{s.rank}</td>
                <td className="px-5 py-3 font-mono text-[#B8BAC4]">
                  {s.assoc === "WPRA" && s.points != null
                    ? `${s.points.toLocaleString()} pts`
                    : `$${(s.earnings || 0).toLocaleString()}`}
                </td>
                <td className="px-5 py-3 font-mono text-[#9A9CA6]">{s.rodeo_count ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {events && events.length > 0 && (
        <div>
          <h2 className="font-display font-bold text-xl text-[#F3EFE6] mb-4">Recent Rodeo Intel</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {events.map((ev) => <IntelCard key={ev.id} ev={ev} />)}
          </div>
        </div>
      )}
    </div>
  );
};


const BRIEF_SECTIONS = [
  { key: "career", label: "Career", icon: Trophy },
  { key: "current_season", label: "Current Season", icon: TrendingUp },
  { key: "horses", label: "Horses & Livestock", icon: Beef },
  { key: "road_life", label: "Road Life", icon: Route },
  { key: "family", label: "Family & Background", icon: Users },
  { key: "outside_rodeo", label: "Outside Rodeo", icon: Dumbbell },
  { key: "sponsors", label: "Sponsors", icon: Briefcase },
  { key: "social", label: "Social Intelligence", icon: Megaphone },
  { key: "interview_history", label: "Interview History", icon: Mic },
];

const RefChips = ({ refs, items }) => {
  if (!refs || refs.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 ml-1.5 align-middle">
      {refs.map((r, i) => {
        const it = items[r - 1];
        if (!it || !it.url) return null;
        const m = platformMeta(it.platform);
        const Icon = m.icon;
        return (
          <a
            key={i}
            href={it.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="brief-source-link"
            title={`${m.label}${it.who ? ` · ${it.who}` : ""}${it.date ? ` · ${it.date}` : ""}: ${it.headline}`}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#1A1D26] border border-[#262933] hover:border-[#E8833A]/50 transition-colors"
            style={{ color: m.color }}
          >
            <Icon className="h-3 w-3" />
            <ExternalLink className="h-2.5 w-2.5 text-[#686B77]" />
          </a>
        );
      })}
    </span>
  );
};

const BriefLine = ({ row, items, testid }) => {
  const text = typeof row === "string" ? row : row?.text;
  const refs = typeof row === "string" ? [] : row?.refs;
  if (!text) return null;
  return (
    <li data-testid={testid} className="text-sm text-[#B8BAC4] leading-relaxed flex flex-wrap items-baseline">
      <span className="text-[#E8833A] mr-2">•</span>
      <span className="flex-1">{text}<RefChips refs={refs} items={items} /></span>
    </li>
  );
};

const PresenterBriefTab = ({ brief, events, athleteId, onRefresh, running }) => {
  const [busy, setBusy] = useState(false);

  const regen = async () => {
    setBusy(true);
    try {
      await api.regenBrief(athleteId);
      toast.success("Presenter brief updated");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the brief");
    }
    setBusy(false);
  };

  const latest = useMemo(() => {
    const buckets = { 7: [], 30: [], 90: [] };
    rankEvents(events).forEach((ev) => {
      const s = freshnessScore(ev);
      if (s === 4) buckets[7].push(ev);
      else if (s === 3) buckets[30].push(ev);
      else if (s === 2) buckets[90].push(ev);
    });
    return buckets;
  }, [events]);

  if (!brief) {
    return (
      <div className="text-center py-20 rounded-xl border border-dashed border-[#262933] bg-[#0F1116]" data-testid="brief-empty">
        <Sparkles className="h-8 w-8 text-[#686B77] mx-auto mb-4" />
        <p className="text-[#F3EFE6] font-display text-lg">No presenter brief yet</p>
        <p className="text-[#9A9CA6] text-sm mt-1 mb-5">
          {events.length === 0
            ? "Run discovery first, then build the announcer's brief."
            : "Synthesize a 5-things / conversation-starters / questions brief from the gathered intel."}
        </p>
        {events.length > 0 && (
          <button
            data-testid="brief-generate-button"
            onClick={regen}
            disabled={busy || running}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Build presenter brief
          </button>
        )}
      </div>
    );
  }

  const items = brief.items || [];
  const sections = brief.sections || {};

  return (
    <div className="space-y-8" data-testid="presenter-brief">
      <div className="flex items-center justify-between gap-3">
        <div className="font-mono text-[11px] text-[#686B77]">
          {brief.generated_at ? `Brief generated ${relativeDate(brief.generated_at)}` : ""}
        </div>
        <button
          data-testid="brief-refresh-button"
          onClick={regen}
          disabled={busy || running}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#1A1D26] border border-[#262933] text-[#F3EFE6] hover:border-[#E8833A]/50 transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh brief
        </button>
      </div>

      {/* 5 Things */}
      {(brief.five_things || []).length > 0 && (
        <section data-testid="brief-five-things">
          <div className="flex items-center gap-2 mb-4">
            <Star className="h-5 w-5 text-[#E8833A]" />
            <h2 className="font-display font-bold text-xl text-[#F3EFE6]">5 Things To Know</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {brief.five_things.map((r, i) => (
              <div key={i} data-testid="brief-thing" className="flex gap-3 rounded-xl border border-[#262933] bg-[#12141A] p-4">
                <span className="h-7 w-7 shrink-0 rounded-full bg-[#E8833A] text-[#0B0D11] text-sm font-bold flex items-center justify-center">{i + 1}</span>
                <p className="text-sm text-[#F3EFE6] leading-relaxed">{r.text}<RefChips refs={r.refs} items={items} /></p>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversation starters */}
        {(brief.conversation_starters || []).length > 0 && (
          <section data-testid="brief-starters" className="rounded-xl border border-[#262933] bg-[#12141A] p-5">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="h-5 w-5 text-[#4ADE80]" />
              <h3 className="font-display font-bold text-lg text-[#F3EFE6]">Great Conversation Starters</h3>
            </div>
            <ul className="space-y-2.5">
              {brief.conversation_starters.map((r, i) => (
                <BriefLine key={i} row={r} items={items} testid="brief-starter" />
              ))}
            </ul>
          </section>
        )}

        {/* Questions to ask */}
        {(brief.questions_to_ask || []).length > 0 && (
          <section data-testid="brief-questions" className="rounded-xl border border-[#262933] bg-[#12141A] p-5">
            <div className="flex items-center gap-2 mb-3">
              <HelpCircle className="h-5 w-5 text-[#7FB2E0]" />
              <h3 className="font-display font-bold text-lg text-[#F3EFE6]">Questions To Ask</h3>
            </div>
            <ul className="space-y-2.5">
              {brief.questions_to_ask.map((r, i) => (
                <BriefLine key={i} row={r} items={items} testid="brief-question" />
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Latest intelligence 7/30/90 */}
      {(latest[7].length + latest[30].length + latest[90].length) > 0 && (
        <section data-testid="brief-latest">
          <div className="flex items-center gap-2 mb-4">
            <Flame className="h-5 w-5 text-[#FB923C]" />
            <h2 className="font-display font-bold text-xl text-[#F3EFE6]">Latest Intelligence</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[["Last 7 days", 7], ["Last 30 days", 30], ["Last 90 days", 90]].map(([label, k]) => (
              <div key={k} className="rounded-xl border border-[#262933] bg-[#12141A] p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#686B77] mb-2">{label} · {latest[k].length}</div>
                {latest[k].length === 0 ? (
                  <div className="text-xs text-[#686B77]">Nothing new</div>
                ) : (
                  <ul className="space-y-2">
                    {latest[k].slice(0, 6).map((ev) => (
                      <li key={ev.id} className="text-sm text-[#B8BAC4] leading-snug">
                        <span className="text-[#F3EFE6]">{ev.headline}</span>
                        <span className="block font-mono text-[10px] text-[#686B77]">{relativeDate(ev.event_date || ev.sources?.[0]?.published_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Structured sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {BRIEF_SECTIONS.map((s) => {
          const rows = sections[s.key] || [];
          if (rows.length === 0) return null;
          return (
            <section key={s.key} data-testid={`brief-section-${s.key}`} className="rounded-xl border border-[#262933] bg-[#12141A] p-5">
              <div className="flex items-center gap-2 mb-3">
                <s.icon className="h-4.5 w-4.5 text-[#E8833A]" />
                <h3 className="font-display font-semibold text-[15px] text-[#F3EFE6]">{s.label}</h3>
              </div>
              <ul className="space-y-2">
                {rows.map((r, i) => (
                  <BriefLine key={i} row={r} items={items} />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
};
